
import React, { useState, useEffect, useContext, useCallback } from "react";
import toast from "react-hot-toast";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaEdit } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { CommonContext } from "../../../App";
import Pagination, { usePagination } from "../../../Components/CommonComponent/Pgination/pagination";
import AddButton from "../../../Components/CommonComponent/Button/AddButton";
import CreateGroup from "../../TypesAndGroups/Groups/CreateGroup";
import DeleteDataModal from "../../../Components/CommonComponent/Modals/DeleteDataModal";
import api from "../../../services/api";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";

export default function CategoryTable() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState({ allData: [], pagination: { totalRecords: 0 } });
  
  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupNameInHindi, setGroupNameInHindi] = useState("");
  const [groupValidation, setGroupValidation] = useState(false);
  const [groupTypeValidation, setGroupTypeValidation] = useState(false);
  
  // Loading States
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Edit & Batch States
  const [editMode, setEditMode] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [groupBatch, setGroupBatch] = useState([]); //  Batch state added

  // Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [checkSearchTerm, setCheckSearchTerm] = useState(false);
  
  // Delete Modal State
  const [modalDelete, setModalDelete] = useState(false);

  const { branchId } = useContext(CommonContext);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";

  // Fixed Type for Categories
  const types = [{ name: "Stock", nameInHindi: "स्टॉक" }];
  const [groupType, setGroupType] = useState(types[0]);

  const {
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    getPaginatedData,
  } = usePagination(10);

  const paginationData = getPaginatedData(groups);

  useEffect(() => {
    if (branchId) {
      fetchGroups();
    }
  }, [currentPage, itemsPerPage, branchId]);

  const fetchGroups = async (search = "") => {
    setLoadingData(true);
    try {
      const res = await api.get(
        `groups-types/get-all-group?page=${currentPage}&limit=${itemsPerPage}&branchId=${branchId}&type=Stock&search=${search}`
      );

      if (res.data.success) {
        const payload = res.data;
        let allData = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.allData)
          ? payload.allData
          : [];

        // Clean and Filter
        allData = allData.map((item) => ({
          ...item,
          type: item.type?.trim(),
          groupName: item.groupName?.trim(),
          category: item.category?.trim(),
        })).filter((item) => item.type === "Stock");

        const totalRecords = payload?.totalRecords ?? payload?.pagination?.totalRecords ?? allData.length;
        setGroups({ allData, pagination: { totalRecords } });
      }
    } catch (err) {
      console.error("Error fetching groups", err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (searchTerm.length === 0) {
      setCheckSearchTerm(false);
    }
  };

  useEffect(() => {
    if (checkSearchTerm && searchTerm.trim() === "") {
      handlePageChange(1);
      fetchGroups("");
    }
  }, [searchTerm]);

  const getSerialNumber = useCallback(
    (index) => (currentPage - 1) * itemsPerPage + index + 1,
    [currentPage, itemsPerPage]
  );

  // ✅ Updated handleSave (Matches AddGroup Logic)
  const handleSave = async () => {
    if (loading) return;

    // ─── EDIT MODE: single update ─────────────────────────────────────────
    if (editMode && selectedLedger?.id) {
      setLoading(true);
      try {
        const payload = {
            groupName,
            groupNameInHindi,
            type: groupType.name,
            typeInHindi: groupType.nameInHindi,
            branchId,
        };

        const response = await api.post(
          `groups-types/update-group?id=${selectedLedger.id}`,
          payload
        );

        if (response.data?.success) {
          toast.success(response.data?.message || "Group updated");
          fetchGroups();
          handleCancel();
        } else {
          toast.error(response.data?.message || "Update failed");
        }
      } catch (err) {
        console.error("Error updating group", err);
        toast.error(err?.response?.data?.message || "Error updating group");
      } finally {
        setLoading(false);
      }
      return;
    }

    // ─── CREATE MODE: batch or single ────────────────────────────────────
    let finalBatch = [...groupBatch];

    // If current form has data, add it to the batch
    if (groupName.trim()) {
      const currentItem = {
        groupName,
        groupNameInHindi,
        type: groupType.name,
        typeInHindi: groupType.nameInHindi,
      };
      finalBatch.push(currentItem);
    }

    if (finalBatch.length === 0) {
      toast.error("Please add at least one category");
      return;
    }

    // Strip internal keys if any
    const cleanBatch = finalBatch.map(({ _groupType, ...rest }) => rest);

    setLoading(true);
    try {
      const response = await api.post(`groups-types/new-group`, {
        branchId,
        groups: cleanBatch,
      });

      if (response.data?.success) {
        toast.success(
          response.data?.message ||
            `${cleanBatch.length} categor${cleanBatch.length > 1 ? "ies" : "y"} added successfully`
        );
        setGroupBatch([]);
        fetchGroups();
        handleCancel();
      } else {
        toast.error(response.data?.message || "Failed to create categories");
      }
    } catch (err) {
      console.error("Error saving groups", err);
      toast.error(err?.response?.data?.message || "Failed to create categories");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setGroupTypeValidation(false);
    setGroupValidation(false);
    setIsModalOpen(false);
    setGroupName("");
    setGroupNameInHindi("");
    setGroupType(types[0]);
    setSelectedLedger(null);
    setEditMode(false);     // Reset edit mode
    setGroupBatch([]);      // Reset batch
  };

  const handleAddGroup = () => {
    const trimmedValue = searchTerm.trim();
    handlePageChange(1);

    if (trimmedValue.length > 0) {
      fetchGroups(trimmedValue);
      setCheckSearchTerm(true);
    } else {
      setIsModalOpen(true);
      setCheckSearchTerm(false);
      setEditMode(false);   // Ensure edit mode is off
      setGroupBatch([]);    // Clear old batch
    }
  };

  const handleEditLedger = useCallback(
    (group) => {
      setSelectedLedger(group);
      setGroupName(group.groupName);
      setGroupNameInHindi(group.groupNameInHindi);
      // Since specific to Stock, ensure type is set correctly
      const typeObj = types.find((t) => t.name === group.type) || types[0];
      setGroupType(typeObj);
      setIsModalOpen(true);
      setEditMode(true);    // Turn on edit mode
    },
    [types]
  );

  const handleDeleteLedger = useCallback((ledger) => {
    setSelectedLedger(ledger);
    setModalDelete(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setModalDelete(false);
    setSelectedLedger(null);
  }, []);

  return (
    <div className="py-2 flex justify-center">
      <div className="flex flex-col justify-center w-full">
        <div className="flex justify-between py-[2px] items-center bg-theme-headerBg px-2 text-theme-color">
          <div className="font-medium">{t("categories")}</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search category..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="px-2 py-[2px] text-sm rounded-1 border border-gray-300 focus:outline-none text-black outline-none"
            />
            <AddButton
              onClick={handleAddGroup}
              text={searchTerm.trim() ? "Search" : t("add")}
            />
          </div>
        </div>

        {loadingData ? (
          <div className="flex justify-center items-center w-full py-12">
            <LoadingSpinner size={70} color="#be8024" />
          </div>
        ) : (groups?.pagination?.totalRecords ?? 0) === 0 ? (
          <p className="text-gray-500 italic text-center mt-5 pt-3">
            {t("noDataAvailable")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapserounded-lg text-sm border-separate border-spacing-0 rounded-b">
              <thead>
                <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                  <th className="border-b px-1 py-[2px] font-medium text-sm text-center">
                    {t("srNumber")}
                  </th>
                  <th className="border-b px-1 font-medium text-sm text-left">
                    {t("groupName")}
                  </th>
                  <th className="border-b px-1 font-medium text-sm text-center">
                    {t("type")}
                  </th>
                  <th className="border-b px-1 font-medium text-sm text-center">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginationData.currentItems?.map((group, index) => (
                  <tr
                    key={group.id || group.groupId}
                    className="bg-theme-tableBg hover:bg-theme-tableHoverColor"
                  >
                    <td className="border-b py-[2px] px-1 text-center">
                      {getSerialNumber(index)}
                    </td>
                    <td className="border-b py-[2px] px-1">
                      {group.groupName || "-"}{" "}
                      {language !== "en" &&
                        group.groupNameInHindi &&
                        ` ${group.groupNameInHindi}`}
                    </td>
                    <td className="border-b py-[2px] px-1 text-center">
                      {group.type || "-"}{" "}
                      {language !== "en" &&
                        group.type_in_hindi &&
                        ` (${group.type_in_hindi})`}
                    </td>
                    <td className="border-b py-[2px] px-1">
                      <div className="flex justify-center gap-2">
                        <FaEdit
                          title="Edit category"
                          onClick={
                            group.branch_id !== 0
                              ? () => handleEditLedger(group)
                              : undefined
                          }
                          className={
                            group.branch_id !== 0
                              ? "text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                              : "text-gray-400 mt-1 opacity-50 pointer-events-none"
                          }
                        />
                        <RiDeleteBin6Fill
                          title="Delete category"
                          onClick={
                            group.branch_id !== 0
                              ? () => handleDeleteLedger(group)
                              : undefined
                          }
                          className={
                            group.branch_id !== 0
                              ? "text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                              : "text-red-400 mt-1 opacity-50 pointer-events-none"
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              data={groups}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 50, 100]}
              showItemsPerPageSelector
              showPageInfo
              position="bottom-center"
            />
          </div>
        )}
      </div>

      {/* ✅ CreateGroup Modal with Batch support */}
      {isModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/20 bg-opacity-50 z-[1000]">
          <CreateGroup
            groupName={groupName}
            setGroupName={setGroupName}
            groupNameInHindi={groupNameInHindi}
            setGroupNameInHindi={setGroupNameInHindi}
            groupValidation={groupValidation}
            groupType={groupType}
            setGroupType={setGroupType}
            groupTypeValidation={groupTypeValidation}
            setGroupTypeValidation={setGroupTypeValidation}
            types={types}
            handleSave={handleSave}
            handleCancel={handleCancel}
            setGroupValidation={setGroupValidation}
            selectedLedger={selectedLedger}
            loading={loading}
            setLoading={setLoading}
            // ✅ New Props for Batch & Edit Mode
            addMoreButton={true}
            editMode={editMode}
            groupBatch={groupBatch}
            setGroupBatch={setGroupBatch}
          />
        </div>
      )}

      {modalDelete && (
        <DeleteDataModal
          selectedLedger={selectedLedger}
          handleDeleteModalClose={handleDeleteModalClose}
          handleDeleteLedgerApi={async (ledger) => {
            try {
              const res = await api.post(
                `groups-types/delete-group?id=${ledger.id}`,
                { isDelete: 1, branchId }
              );
              if (res.data.success) {
                toast.success(res.data.message || "Group deleted successfully");
                fetchGroups();
              } else {
                toast.error(res.data.message || "Failed to delete group");
              }
            } catch (err) {
              console.error("Error deleting Group", err);
              toast.error(err?.response?.data?.message || "error deleting group");
            } finally {
              handleDeleteModalClose();
            }
          }}
        />
      )}
    </div>
  );
}